import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Polygon, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useAlerts } from '../contexts/AlertsContext';
import { useToast } from '../contexts/ToastContext';
import propertiesService from '../services/propertiesService';
import alertsService from '../services/alertsService';
import { getDirections } from '../services/directionsService';
import './AlertDetail.css';



const CATEGORIES = [
  { key: 'gate',      label: 'Gate / Access', color: '#f59e0b', emoji: '' },
  { key: 'water',     label: 'Water Source',  color: '#3b82f6', emoji: '' },
  { key: 'hazard',    label: 'Hazard',        color: '#ef4444', emoji: ''  },
  { key: 'livestock', label: 'Livestock Pen', color: '#22c55e', emoji: '' },
];
const cat = key => CATEGORIES.find(c => c.key === key);

function FitBounds({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length >= 2) map.fitBounds(positions, { padding: [30, 30] });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function PropertyMap({ boundary, features }) {
  const positions = (boundary ?? []).map(p => [p.lat, p.lng]);
  return (
    <div className="detail-map">
      <MapContainer center={[41.5, -99.9]} zoom={7} style={{ height: '320px', width: '100%' }} scrollWheelZoom>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' />
        {positions.length >= 2 && <FitBounds positions={positions} />}
        {positions.length >= 3 && (
          <Polygon positions={positions}
            pathOptions={{ color: '#fe3130', fillColor: '#fe3130', fillOpacity: 0.15, weight: 2 }} />
        )}
        {(features ?? []).map(pin => (
          <CircleMarker key={pin.id} center={[pin.lat, pin.lng]} radius={7}
            pathOptions={{ color: cat(pin.category)?.color, fillColor: cat(pin.category)?.color, fillOpacity: 0.9, weight: 2 }}>
            <Tooltip direction="top" offset={[0, -8]}>
              <strong>{cat(pin.category)?.emoji} {pin.label}</strong>
              {pin.notes && <><br /><span>{pin.notes}</span></>}
            </Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────
function AlertDetail() {
  const { id }                       = useParams();
  const navigate                     = useNavigate();
  const { myAlerts, nearbyAlerts }   = useAlerts();
  const { addToast }                 = useToast();

  const [alert,          setAlert]          = useState(null);
  const [isOwner,        setIsOwner]        = useState(false);
  const [property,       setProperty]       = useState(null);
  const [covered,        setCovered]        = useState({});
  const [responders,     setResponders]     = useState([]);
  const [iResponded,     setIResponded]     = useState(false);
  const [showRespondModal, setShowRespondModal] = useState(false);
  const [respondMessage, setRespondMessage] = useState('');
  const [respondEta,     setRespondEta]     = useState('');
  const [isSavingResp,   setIsSavingResp]   = useState(false);
  const [cantMakeIt,     setCantMakeIt]     = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [selectedSkills,    setSelectedSkills]    = useState([]);

  const closeRespondModal = () => {
    setShowRespondModal(false);
    setRespondMessage('');
    setRespondEta('');
    setCantMakeIt(false);
    setSelectedEquipment([]);
    setSelectedSkills([]);
  };

  useEffect(() => {
    // Determine ownership and seed i_responded immediately from context
    // so the action bar renders correctly before the getAlert fetch resolves.
    const inMine   = myAlerts.find(a => String(a.id) === String(id));
    const inNearby = nearbyAlerts.find(a => String(a.id) === String(id));
    setIsOwner(!!inMine);
    if (inNearby) setIResponded(inNearby.i_responded ?? false);

    // Fetch the full alert (includes responders + definitive i_responded)
    alertsService.getAlert(id)
      .then(found => {
        setAlert(found);
        setIResponded(found.i_responded ?? false);  // overwrites context seed
        setResponders(found.responders ?? []);

        const initial = {};
        (found.neededEquipment ?? []).forEach(e => { initial[`eq-${e.id}`] = e.covered; });
        (found.neededSkills    ?? []).forEach(s => { initial[`sk-${s.id}`] = s.covered; });
        setCovered(initial);

        if (found.property_id) {
          propertiesService.getProperty(found.property_id)
            .then(p => setProperty(p))
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [id, myAlerts, nearbyAlerts]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleEquipment = (itemId) => {
    setSelectedEquipment(prev =>
      prev.includes(itemId) ? prev.filter(x => x !== itemId) : [...prev, itemId]
    );
  };

  const toggleSkill = (itemId) => {
    setSelectedSkills(prev =>
      prev.includes(itemId) ? prev.filter(x => x !== itemId) : [...prev, itemId]
    );
  };

  const handleRespond = async (e) => {
    e.preventDefault();
    setIsSavingResp(true);
    try {
      await alertsService.respond(
        Number(id),
        respondMessage,
        respondEta,
        selectedEquipment,
        selectedSkills
      );
      setIResponded(true);
      closeRespondModal();
      addToast('Your response has been recorded!', 'success');
      // Refresh to pick up the updated responders list (owner sees it)
      alertsService.getAlert(id).then(found => setResponders(found.responders ?? [])).catch(() => {});
    } catch (err) {
      addToast(err.status === 403 ? 'You cannot respond to your own alert.' : 'Could not submit — please try again.', 'error');
    } finally {
      setIsSavingResp(false);
    }
  };

  const handleWithdraw = async () => {
    try {
      await alertsService.withdrawResponse(Number(id));
      setIResponded(false);
      closeRespondModal();
      addToast("Got it — your response has been withdrawn.", 'success');
      alertsService.getAlert(id).then(found => setResponders(found.responders ?? [])).catch(() => {});
    } catch {
      addToast('Could not withdraw response — please try again.', 'error');
    }
  };

  const toggleCovered = async (key, type, catalogId) => {
    const newValue = !covered[key];
    setCovered(prev => ({ ...prev, [key]: newValue }));       // optimistic
    try {
      await alertsService.setCoverage(Number(id), type, catalogId, newValue);
    } catch {
      setCovered(prev => ({ ...prev, [key]: !newValue }));    // revert
      addToast('Could not update coverage — please try again.', 'error');
    }
  };

  const centroid = boundary => {
    if (!boundary?.length) return null;
    return {
      lat: boundary.reduce((s, p) => s + p.lat, 0) / boundary.length,
      lng: boundary.reduce((s, p) => s + p.lng, 0) / boundary.length,
    };
  };

  if (!alert) return (
    <div className="alert-detail-page"><div className="container">
      <p>Alert not found.</p>
      <button className="button" onClick={() => navigate('/alerts')}>← Back to Alerts</button>
    </div></div>
  );

  const dest = property ? centroid(property.boundary) : alert.coordinates;

  // Map responder-selected catalog IDs to human-readable labels from the
  // alert's needed equipment/skills lists.
  const equipmentLabelMap = Object.fromEntries((alert.neededEquipment ?? []).map(e => [e.id, e.label]));
  const skillLabelMap     = Object.fromEntries((alert.neededSkills    ?? []).map(s => [s.id, s.label]));

  return (
    <div className="alert-detail-page">
      <div className="container">

        {/* ── Header ────────────────────────────────────────────────────── */}
        <div className="detail-page-header">
          <button className="button back-button" onClick={() => navigate('/alerts')}>Return to Alerts</button>
          <div className="alert-detail-title">
            <h1>{alert.type}</h1>
            <span className="alert-location-label">{alert.location}</span>
          </div>
          <span className={`severity-badge severity-${(alert.severity || '').toLowerCase()}`}>{alert.severity}</span>
        </div>

        {/* ── 911 Banner ────────────────────────────────────────────────── */}
        {alert.severity === 'High' && (
          <div className="banner-911">
            🚨 High severity — call 911 if there is immediate danger to life or property.
          </div>
        )}

        {/* ── Map ───────────────────────────────────────────────────────── */}
        {property && (
          <>
            <PropertyMap boundary={property.boundary} features={property.features} />
            <div className="map-legend">
              {CATEGORIES.map(c => (
                <span key={c.key} className="legend-item">
                  <span className="legend-dot" style={{ background: c.color }} />
                  {c.emoji} {c.label}
                </span>
              ))}
            </div>
          </>
        )}

        {/* ── Resources grid ────────────────────────────────────────────── */}
        <div className="resources-grid">

          {/* Equipment */}
          <section className="resource-section">
            <h2>Needed Equipment</h2>
            {(alert.neededEquipment ?? []).length === 0
              ? <p className="none-needed">None specified</p>
              : (alert.neededEquipment ?? []).map(item => {
                  const key = `eq-${item.id}`;
                  return (
                    <div key={key} className={`resource-item ${covered[key] ? 'covered' : ''}`}>
                      <span>{item.label}</span>
                      {isOwner && (
                        <button className={`coverage-btn ${covered[key] ? 'covered' : ''}`}
                          onClick={() => toggleCovered(key, 'equipment', item.id)}>
                          {covered[key] ? '✓ We have enough' : 'We have enough?'}
                        </button>
                      )}
                      {!isOwner && covered[key] && <span className="covered-badge">✓ Covered</span>}
                    </div>
                  );
                })
            }
          </section>

          {/* Skills */}
          <section className="resource-section">
            <h2>Needed Skills</h2>
            {(alert.neededSkills ?? []).length === 0
              ? <p className="none-needed">None specified</p>
              : (alert.neededSkills ?? []).map(item => {
                  const key = `sk-${item.id}`;
                  return (
                    <div key={key} className={`resource-item ${covered[key] ? 'covered' : ''}`}>
                      <span>{item.label}</span>
                      {isOwner && (
                        <button className={`coverage-btn ${covered[key] ? 'covered' : ''}`}
                          onClick={() => toggleCovered(key, 'skill', item.id)}>
                          {covered[key] ? '✓ We have enough' : 'We have enough?'}
                        </button>
                      )}
                      {!isOwner && covered[key] && <span className="covered-badge">✓ Covered</span>}
                    </div>
                  );
                })
            }
          </section>
        </div>

        {/* ── Responders (owner-only) ───────────────────────────────────── */}
        {isOwner && (
          <section className="responders-section">
            <h2>Responding Helpers ({responders.length})</h2>
            {responders.length === 0
              ? <p className="none-needed">No helpers have responded yet.</p>
              : (
                <div className="responder-list">
                  {responders.map(r => (
                    <div key={r.id} className="responder-card">
                      <div className="responder-name">
                        {r.name}
                        {r.eta && <span className="responder-eta-badge">{r.eta}</span>}
                      </div>
                      {r.message && <div className="responder-message">{r.message}</div>}
                      {(r.equipment ?? []).length > 0 && (
                        <div className="responder-tags">
                          {r.equipment.map(id => (
                            <span key={id} className="tag tag-eq">{equipmentLabelMap[id] ?? `Equipment ${id}`}</span>
                          ))}
                        </div>
                      )}
                      {(r.skills ?? []).length > 0 && (
                        <div className="responder-tags">
                          {r.skills.map(id => (
                            <span key={id} className="tag tag-sk">{skillLabelMap[id] ?? `Skill ${id}`}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )
            }
          </section>
        )}

        {/* ── Actions ───────────────────────────────────────────────────── */}
        <div className="alert-detail-actions">
          {!isOwner && !iResponded && (
            <button className="button response-button" onClick={() => setShowRespondModal(true)}>
              <i className="fas fa-hand-paper"></i> I can help!
            </button>
          )}
          {!isOwner && iResponded && (
            <button className="button update-response-button" onClick={() => setShowRespondModal(true)}>
              ✓ You're responding — manage
            </button>
          )}
          {dest && (
            <button className="button directions-button" onClick={() => getDirections(dest, alert.location)}>
              <span className="material-icons" style={{ fontSize: '1rem', verticalAlign: 'middle' }}>directions_car</span>
              {' '}Get Directions
            </button>
          )}
          {(isOwner || iResponded) && (
            <button className="button incident-map-button" onClick={() => navigate(`/alerts/${id}/incident-map`)}>
              <span className="material-icons" style={{ fontSize: '1rem', verticalAlign: 'middle' }}>map</span>
              {' '}Incident Map
            </button>
          )}
          {isOwner && alert.status === 'active' && (
            <button className="button edit-button" onClick={() => navigate(`/alerts/${id}/edit`)}>
              <span className="material-icons" style={{ fontSize: '1rem', verticalAlign: 'middle' }}>edit</span>
              {' '}Edit Alert
            </button>
          )}
          {isOwner && (
            <button className="button dismiss-button" onClick={() => navigate('/alerts')}>
              Close Alert
            </button>
          )}
        </div>

        {/* ── Respond modal ─────────────────────────────────────────────── */}
        {showRespondModal && (
          <div className="response-modal" onClick={closeRespondModal}>
            <div className="response-modal-content" onClick={e => e.stopPropagation()}>
              <div className="response-modal-header">
                <h3>{iResponded ? 'Update your response' : 'Confirm your response'}</h3>
                <button className="close-button" onClick={closeRespondModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <form onSubmit={cantMakeIt ? e => { e.preventDefault(); handleWithdraw(); } : handleRespond} className="response-form">
                <div className="form-group">
                  <label htmlFor="detail-response-eta">
                    Your ETA <span className="label-optional">(optional)</span>
                  </label>
                  <select
                    id="detail-response-eta"
                    className="response-select"
                    value={respondEta}
                    onChange={e => setRespondEta(e.target.value)}
                    disabled={cantMakeIt}
                  >
                    <option value="">— Select ETA —</option>
                    <option value="15 min">15 min</option>
                    <option value="30 min">30 min</option>
                    <option value="45 min">45 min</option>
                    <option value="1 hr">1 hr</option>
                    <option value="2 hr">2 hr</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="detail-response-message">
                    Message for the rancher <span className="label-optional">(optional)</span>
                  </label>
                  <textarea
                    id="detail-response-message"
                    className={`response-textarea${cantMakeIt ? ' response-textarea--muted' : ''}`}
                    rows={3}
                    placeholder="e.g. Bringing a livestock trailer, ETA 20 min"
                    value={respondMessage}
                    onChange={e => setRespondMessage(e.target.value)}
                    disabled={cantMakeIt}
                  />
                </div>
                {(alert.neededEquipment?.length > 0 || alert.neededSkills?.length > 0) && (
                  <div className="form-group">
                    <label>What can you bring? <span className="label-optional">(optional)</span></label>
                    <div className="response-chips">
                      {(alert.neededEquipment ?? []).map(item => (
                        <button
                          key={`resp-eq-${item.id}`}
                          type="button"
                          className={`response-chip response-chip-eq ${selectedEquipment.includes(item.id) ? 'selected' : ''}`}
                          onClick={() => toggleEquipment(item.id)}
                          disabled={cantMakeIt}
                        >
                          {selectedEquipment.includes(item.id) ? '✓ ' : ''}{item.label}
                        </button>
                      ))}
                      {(alert.neededSkills ?? []).map(item => (
                        <button
                          key={`resp-sk-${item.id}`}
                          type="button"
                          className={`response-chip response-chip-sk ${selectedSkills.includes(item.id) ? 'selected' : ''}`}
                          onClick={() => toggleSkill(item.id)}
                          disabled={cantMakeIt}
                        >
                          {selectedSkills.includes(item.id) ? '✓ ' : ''}{item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {iResponded && (
                  <label className="cant-make-it-toggle">
                    <input
                      type="checkbox"
                      checked={cantMakeIt}
                      onChange={e => setCantMakeIt(e.target.checked)}
                    />
                    <span>I can't make it after all</span>
                  </label>
                )}
                {cantMakeIt ? (
                  <button type="submit" className="btn-withdraw-confirm" disabled={isSavingResp}>
                    {isSavingResp ? 'Withdrawing…' : 'Withdraw my response'}
                  </button>
                ) : (
                  <button type="submit" className="btn-save" disabled={isSavingResp}>
                    {isSavingResp ? 'Submitting…' : (iResponded ? 'Update response' : "I can help!")}
                  </button>
                )}
              </form>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default AlertDetail;
